import React, { useState } from "react";
import { toast } from "sonner";
import { MapPin, Mail, Clock, ArrowRight, Instagram, HandCoins } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { sendContact } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PhoneField, DEFAULT_COUNTRY } from "@/components/PhoneField";
import { SOCIAL, MAPS_URL } from "@/lib/constants";

export const Contact = () => {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fullPhone = phone.trim() ? `${country.dial} ${phone.trim()}` : "";
      await sendContact({ ...form, phone: fullPhone, language: lang });
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", subject: "", message: "" });
      setPhone("");
    } catch (err) {
      toast.error(t("contact.error"));
    } finally {
      setSending(false);
    }
  };

  const fieldCls =
    "bg-transparent border-0 border-b border-white/25 rounded-none px-0 py-3 h-auto text-[#eef3e6] placeholder:text-[#eef3e6]/50 focus-visible:ring-0 focus-visible:border-[#a7c084] transition-colors duration-300 font-sans";

  return (
    <section id="contact" className="py-24 md:py-36 bg-[#16260f] text-[#eef3e6] border-t border-border" data-testid="contact">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-16">
          {/* Left: heading, intro + message form */}
          <div className="lg:col-span-7">
            <Reveal>
              <span className="font-sans uppercase tracking-[0.25em] text-xs font-semibold text-[#a7c084]">
                {t("contact.overline")}
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-serif text-5xl sm:text-7xl tracking-tight leading-none">
                {t("contact.title")}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 font-sans text-base leading-relaxed font-light opacity-80 max-w-md">
                {t("contact.intro")}
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <form onSubmit={onSubmit} data-testid="contact-form" className="mt-10 flex flex-col gap-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <Input
                    name="name" required value={form.name} onChange={onChange}
                    placeholder={t("contact.name")} data-testid="contact-name" className={fieldCls}
                  />
                  <Input
                    name="email" type="email" required value={form.email} onChange={onChange}
                    placeholder={t("contact.email")} data-testid="contact-email" className={fieldCls}
                  />
                </div>
                <PhoneField
                  country={country}
                  number={phone}
                  onCountryChange={setCountry}
                  onNumberChange={setPhone}
                  placeholder={t("contact.phone")}
                  triggerClassName="text-[#eef3e6]"
                  inputClassName="text-[#eef3e6] placeholder:text-[#eef3e6]/50"
                />
                <Input
                  name="subject" value={form.subject} onChange={onChange}
                  placeholder={t("contact.subject")} data-testid="contact-subject" className={fieldCls}
                />
                <Textarea
                  name="message" required rows={5} value={form.message} onChange={onChange}
                  placeholder={t("contact.message")} data-testid="contact-message"
                  className={`${fieldCls} resize-none`}
                />
                <button
                  type="submit" disabled={sending} data-testid="contact-submit"
                  className="group self-start rounded-full bg-[#8aad5d] text-[#16260f] px-8 py-4 font-sans text-sm tracking-wide font-medium hover:bg-[#9cbf6f] transition-colors duration-300 flex items-center gap-2 disabled:opacity-60"
                >
                  {sending ? t("contact.sending") : t("contact.send")}
                  <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              </form>
            </Reveal>
          </div>

          {/* Right: contact info */}
          <div className="lg:col-span-4 lg:col-start-9">
            <div className="space-y-8 lg:pt-2">
              <Reveal delay={0.15} className="flex items-start gap-4">
                <MapPin size={20} strokeWidth={1.5} className="text-[#a7c084] mt-1 shrink-0" />
                <div>
                  <p className="font-sans uppercase tracking-[0.2em] text-[11px] opacity-60 mb-1">{t("contact.addressLabel")}</p>
                  <a
                    href={MAPS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-address-link"
                    className="font-serif text-2xl leading-tight underline underline-offset-4 decoration-current/40 hover:decoration-current transition-[text-decoration-color] duration-300 inline-block"
                  >
                    Steinerstrasse 14<br /><span className="text-xl opacity-90">{t("contact.floor")}</span><br />3006 Bern, {t("contact.country")}
                  </a>
                  <p className="mt-2 font-sans text-xs font-light opacity-70 leading-relaxed max-w-[280px]" data-testid="contact-entrance-note">
                    {t("contact.entranceNote")}
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.2} className="flex items-start gap-4">
                <Mail size={20} strokeWidth={1.5} className="text-[#a7c084] mt-1 shrink-0" />
                <div>
                  <p className="font-sans uppercase tracking-[0.2em] text-[11px] opacity-60 mb-1">{t("contact.emailLabel")}</p>
                  <a href={`mailto:${SOCIAL.email}`} data-testid="contact-email-link" className="font-serif text-2xl link-underline">
                    {SOCIAL.email}
                  </a>
                </div>
              </Reveal>
              <Reveal delay={0.25} className="flex items-start gap-4">
                <Clock size={20} strokeWidth={1.5} className="text-[#a7c084] mt-1 shrink-0" />
                <div>
                  <p className="font-sans uppercase tracking-[0.2em] text-[11px] opacity-60 mb-1">{t("contact.hoursLabel")}</p>
                  <p className="font-serif text-2xl">{t("contact.hours")}</p>
                  <a
                    href="#activities"
                    data-testid="contact-hours-cta"
                    className="mt-2 inline-flex items-center gap-1.5 font-sans text-[11px] uppercase tracking-[0.18em] text-primary hover:text-primary/70 transition-colors"
                  >
                    {t("contact.hoursCta")} <ArrowRight size={12} strokeWidth={2} />
                  </a>
                </div>
              </Reveal>
              <Reveal delay={0.3} className="flex items-start gap-4">
                <Instagram size={20} strokeWidth={1.5} className="text-[#a7c084] mt-1 shrink-0" />
                <div>
                  <p className="font-sans uppercase tracking-[0.2em] text-[11px] opacity-60 mb-2">{t("contact.socialLabel")}</p>
                  <div className="flex flex-col gap-1.5 font-sans text-base">
                    <p>
                      Instagram:{" "}
                      <a
                        href={SOCIAL.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-instagram-link"
                        className="text-[#a7c084] underline underline-offset-4 decoration-[#a7c084]/50 hover:decoration-[#a7c084] hover:text-[#c9dca6] transition-colors duration-300"
                      >
                        @estesia.zentrum
                      </a>
                    </p>
                    <p>
                      Facebook:{" "}
                      <a
                        href={SOCIAL.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-facebook-link"
                        className="text-[#a7c084] underline underline-offset-4 decoration-[#a7c084]/50 hover:decoration-[#a7c084] hover:text-[#c9dca6] transition-colors duration-300"
                      >
                        /estesia.bern
                      </a>
                    </p>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.35} className="flex items-start gap-4" data-testid="donation-block">
                <HandCoins size={20} strokeWidth={1.5} className="text-[#a7c084] mt-1 shrink-0" />
                <div className="flex flex-col sm:flex-row sm:items-start sm:gap-8 flex-1">
                  <div className="flex-1 min-w-0">
                    <p className="font-sans uppercase tracking-[0.2em] text-[11px] opacity-60 mb-1">{t("contact.donateLabel")}</p>
                    <p className="font-sans text-sm font-light opacity-75 mb-2 max-w-xs">{t("contact.donateText")}</p>
                    <p className="font-serif text-xl tracking-wide tabular-nums" data-testid="donation-iban">CH14 0900 0000 3409 1774 3</p>
                    <p className="font-sans text-sm opacity-70 mt-1 leading-relaxed">PostFinance AG · Mingerstrasse 20 · 3030 Bern · Switzerland</p>
                  </div>
                  <Dialog>
                    <DialogTrigger asChild>
                      <button
                        type="button"
                        data-testid="twint-qr"
                        className="mt-4 sm:mt-0 group flex flex-col items-center shrink-0 cursor-zoom-in"
                        aria-label="TWINT QR"
                      >
                        <span className="inline-block h-8">
                          <img src="/twint-logo.png" alt="TWINT" className="h-full w-auto object-contain" />
                        </span>
                        <span className="mt-2 block bg-white p-1.5 rounded-sm ring-1 ring-black/10 shadow-sm group-hover:ring-primary transition-all duration-300">
                          <img src="/twint-qr-small.png" alt="TWINT QR" className="w-[90px] h-[90px] object-cover object-center" />
                        </span>
                        <span className="mt-2 font-sans text-[10px] uppercase tracking-[0.18em] opacity-60 group-hover:opacity-90 transition-opacity">
                          {t("contact.twintLabel")}
                        </span>
                      </button>
                    </DialogTrigger>
                    <DialogContent
                      data-testid="twint-qr-modal"
                      className="max-w-md bg-white text-black border-0 p-8"
                    >
                      <DialogTitle className="sr-only">TWINT QR</DialogTitle>
                      <div className="flex flex-col items-center gap-5">
                        <img src="/twint-logo-dark.png" alt="TWINT" className="h-10 w-auto" />
                        <div className="bg-white p-3 rounded-sm ring-1 ring-black/10">
                          <img src="/twint-qr.png" alt="TWINT QR — Estesia Bern" className="w-[340px] h-[340px] max-w-full object-contain" />
                        </div>
                        <p className="font-serif text-lg text-black">Estesia — Espiritismo</p>
                        <p className="font-sans text-xs uppercase tracking-[0.2em] text-neutral-500 text-center max-w-xs leading-relaxed">
                          {t("contact.twintModalHint")}
                        </p>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
